import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userPreferencesService } from '../services/userPreferences';
import { toast } from 'react-hot-toast';

interface UseUserPreferencesOptions {
  enabled?: boolean;
}

export const useUserPreferences = ({ enabled = true }: UseUserPreferencesOptions = {}) => {
  const queryClient = useQueryClient();

  const preferencesQuery = useQuery({
    queryKey: ['userPreferences'],
    queryFn: userPreferencesService.getPreferences,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: userPreferencesService.updatePreferences,
    onSuccess: (newPreferences) => {
      queryClient.setQueryData(['userPreferences'], newPreferences);
      toast.success('Nastavení uloženo');
    },
    onError: (error: any) => {
      console.error('Failed to update preferences', error);
      toast.error('Chyba při ukládání nastavení');
    },
  });

  return {
    preferences: preferencesQuery.data,
    isLoading: preferencesQuery.isLoading,
    error: preferencesQuery.error,
    updatePreferences: updatePreferencesMutation.mutate,
    isUpdating: updatePreferencesMutation.isPending,
  };
};

export const useFavorites = () => {
  const queryClient = useQueryClient();

  const favoritesQuery = useQuery({
    queryKey: ['userFavorites'],
    queryFn: userPreferencesService.getFavorites,
  });

  const addFavoriteMutation = useMutation({
    mutationFn: ({ type, id }: { type: string; id: string | number }) =>
      userPreferencesService.addFavorite(type, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFavorites'] });
      toast.success('Přidáno do oblíbených');
    },
    onError: () => toast.error('Chyba při přidávání do oblíbených'),
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: ({ type, id }: { type: string; id: string | number }) =>
      userPreferencesService.removeFavorite(type, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFavorites'] });
      toast.success('Odebráno z oblíbených');
    },
    onError: () => toast.error('Chyba při odebírání z oblíbených'),
  });

  return {
    favorites: favoritesQuery.data || [],
    isLoading: favoritesQuery.isLoading,
    addFavorite: addFavoriteMutation.mutate,
    removeFavorite: removeFavoriteMutation.mutate,
  };
};

export const useRecents = () => {
  return useQuery({
    queryKey: ['userRecents'],
    queryFn: userPreferencesService.getRecents,
  });
};
